// src/composables/useQrGenerator.js
import { ref, watch } from 'vue';
import QRCode from 'qrcode';

/**
 * Composable de Vue 3 para generar la Data URL de un Código QR.
 * @param {Ref<string>} urlRef - Una referencia (ref) o valor computado (computed) que contiene la URL/texto a codificar.
 * @returns {{ qrDataUrl: Ref<string | null>, isLoadingQr: Ref<boolean>, error: Ref<string | null>, generateQr: (url: string) => Promise<void> }}
 */
export function useQrGenerator(urlRef) {
    const qrDataUrl = ref(null);
    const isLoadingQr = ref(false);
    const error = ref(null);

    // Hacemos que generateQr devuelva una promesa para que se pueda esperar (await)
    const generateQr = (url) => {
        return new Promise((resolve, reject) => {
            if (!url || url.trim() === '') {
                qrDataUrl.value = null;
                return resolve();
            }

            isLoadingQr.value = true;
            error.value = null;

            QRCode.toDataURL(url.trim(), {
                errorCorrectionLevel: 'H',
                type: 'image/png',
                margin: 2,
                width: 256
            })
            .then(dataUrl => {
                qrDataUrl.value = dataUrl;
                resolve(dataUrl); // Resuelve con la Data URL
            })
            .catch(err => {
                console.error("Error generating QR code:", err);
                error.value = "No se pudo generar el código QR.";
                reject(err);
            })
            .finally(() => {
                isLoadingQr.value = false;
            });
        });
    };

    // 1. Usar 'watch' para reaccionar a cambios en la URL de entrada
    // Lo mantenemos, pero la llamada en el PDF se hará de forma manual y asíncrona.
    watch(urlRef, (newUrl) => {
        // No manejamos la promesa aquí, solo iniciamos el proceso reactivo.
        generateQr(newUrl).catch(() => {});
    }, { immediate: true });

    return { qrDataUrl, isLoadingQr, error, generateQr }; // 🛑 Exportamos generateQr 🛑
}